import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Typography,
  notification,
  Menu,
  Input,
  Dropdown,
  Modal
} from "antd";

import { SearchOutlined , DownOutlined } from "@ant-design/icons";

// Images
import { getVehicles, getVehicleDetail, deleteVehicle } from "../api/vehicles.service";
import { useEffect, useState } from "react";
import moment from "moment";
import EditVehicleFormModal from "../components/forms/vehicles/EditVehicleFormModal";
import AddVehicleFormModal from "../components/forms/vehicles/AddVehicleFormModal";
import AssignDriverToVehicleFormModal from "../components/forms/vehicles/AssignDriverToVehicleFormModal";

const { Title } = Typography

function Vehicles() {

  // Helper to add column search filter functionality
  const getColumnSearchProps = (dataIndex, placeholder, renderField) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${placeholder}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      // Use custom renderField function if provided
      const recordValue = renderField ? renderField(record) : record[dataIndex];
      return recordValue
        ? recordValue.toString().toLowerCase().includes(value.toLowerCase())
        : false;
    },
  });

  const columns = [
    {
      title: "VEHICLE REG NO",
      dataIndex: "registrationNo",
      key: "registrationNo",
      width: "15%",
      ...getColumnSearchProps("registrationNo", "Reg No", (record) => record.registrationNo),
      render: (text, record) => (
        <div className="avatar-info">
          <Title level={5}>{record.registrationNo}</Title>
        </div>
      ),
    },
    {
      title: "CATEGORY",
      dataIndex: "vehicleType",
      key: "vehicleType",
      width: "15%",
      ...getColumnSearchProps("vehicleType", "Type", (record) => record.vehicleType?.name),
      render: (text, record) => (
        <div className="author-info">
          <Title level={5}>{record.vehicleType?.name}</Title>
        </div>
      ),
    },
    {
      title: "MAKE",
      dataIndex: "make",
      key: "make", 
      width: "15%",
      ...getColumnSearchProps("make", "Make", (record) => record.make),
      render: (text, record) => (
        <div className="author-info">
          <Title level={5}>{record.make}</Title>
        </div>
      ),
    },
    {
      title: "MODEL",
      dataIndex: "model",
      key: "model",
      width: "15%", 
      ...getColumnSearchProps("model", "Model", (record) => record.model),
      render: (text, record) => (
        <div className="author-info">
          <Title level={5}>{record.model}</Title>
        </div>
      ),
    },
    {
      title: "COLOR",
      dataIndex: "color",
      key: "color",
      width: "15%",
      ...getColumnSearchProps("color", "Color", (record) => record.color),
      render: (text, record) => (
        <div className="author-info">
          <Title level={5}>{record.color}</Title>
        </div>
      ),
    },
    {
      title: "DRIVER",
      dataIndex: "driver",
      key: "driver",
      width: "15%",
      ...getColumnSearchProps("driver", "Driver", (record) => record.driver?.name || "N/A"),
      render: (text, record) => (
        <div className="author-info">
          <Title level={5}>{record?.driver?.name || "N/A"}</Title>
        </div>
      ),
    },
    {
      title: "ACTIONS",
      width: "15%",
      render: (text, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="view" onClick={() => getVehicleData(record.id, true)}>
              View
            </Menu.Item>
            <Menu.Item key="edit" onClick={() => getVehicleData(record.id)}>
              Edit
            </Menu.Item>
            <Menu.Item key="assign" onClick={() => getVehicleDataAssign(record.id)}>
              Assign
            </Menu.Item>
            <Menu.Item key="delete" onClick={() => getVehicleDataDelete(record.id)}>
              Delete
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu}>
            <a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
              Tasks <DownOutlined />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
  const [isModalOpenAssign, setIsModalOpenAssign] = useState(false);
  const [vehicleData, setVehicleData] = useState(null);
  const [isViewOnly, setisViewOnly] = useState(false);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const data = await getVehicles();
      setVehicles(data?.data || []); // Set the fetched vehicles data
    } catch (err) {
      notification.error({ message: 'Failed to fetch vehicles' });
      setError('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  const getVehicleData = async (id, isViewOnly = false) => {
    setisViewOnly(isViewOnly)
    const data = await getVehicleDetail(id);
    setVehicleData(data.data);
    setIsModalOpenEdit(true);
  }

  const getVehicleDataAssign = async (id) => {
    const data = await getVehicleDetail(id);
    setVehicleData(data.data);
    setIsModalOpenAssign(true);
  }

  const handleConfirm = async (id) => {
    try {
      const data = await deleteVehicle(id);
      
        notification.success({ message: 'Vehicle deleted successfully' });
        fetchVehicles();
      
    } catch (err) {
      notification.error({ message: 'Failed to delete vehicle' });
    }
  }

  const getVehicleDataDelete = async (id) => {
    Modal.confirm({
      title: "Warning",
      content: "Are you sure you want to delete this?",
      okText: "Confirm",
      cancelText: "Cancel",
      onOk() {
          handleConfirm(id)
      },
      onCancel() {
          console.log("Cancelled");
      },
  });
  }

  useEffect(() => {
    fetchVehicles();
  }, []);

  const onChange = (e) => console.log(`radio checked:${e.target.value}`);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleOkEdit = () => {
    setIsModalOpenEdit(false);
  };

  const handleCancelEdit = () => {
    setIsModalOpenEdit(false);
  };

  const handleCancelAssign = () => {
    setIsModalOpenAssign(false);
  };

  const handleOkAssign = () => {

  }

  return (
    <>
      {vehicleData &&
        <AssignDriverToVehicleFormModal vehicleData={vehicleData} fetchAllVehicles={fetchVehicles} handleCancel={handleCancelAssign} handleOk={handleOkAssign} isModalOpen={isModalOpenAssign} setIsModalOpen={setIsModalOpenAssign} />}
      {vehicleData && <EditVehicleFormModal isViewOnly={isViewOnly} vehicleData={vehicleData} fetchAllVehicles={fetchVehicles} handleCancel={handleCancelEdit} handleOk={handleOkEdit} isModalOpen={isModalOpenEdit} setIsModalOpen={setIsModalOpenEdit} />}
      {<AddVehicleFormModal vehicleData={vehicleData} fetchVehiclesTypes={fetchVehicles} handleCancel={handleCancel} handleOk={handleOk} isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />}
      <div className="mb-3 flex justify-end">
        <Button type="primary" onClick={showModal}>
          Add Vehicle
        </Button>
      </div>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="Vehicles Table"
            >
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={vehicles}
                  pagination={{
                    pageSize: 100
                  }}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Vehicles;
